import AppCard from '@nxsys/components/AppCard';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppRateForm, {
  AppRateFormItem,
  calculateNetAndTotalFromRate,
  PAYMENT_RATE_DEFAULT,
} from '@nxsys/components/AppRateForm';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option } from '@nxsys/types/models/Common';
import { isHolidayPayRate } from '@nxsys/utils';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import { Form } from 'antd';
import moment from 'moment';
import { Ref, useImperativeHandle, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCurrentTaxPeriodByFrequency,
  getOptionsAllTaxWeekTaxMonth,
} from '@nxsys/utils/taxYear';
import { IRELAND } from '@nxsys/store/constants/keys';

export type TimesheetEmployeeFormValues = {
  assignmentRef: Option<string> | null;
  agencyRef: Option<string> | null;
  startDate: string | null;
  endDate: string | null;
  siteId: string | null;
  taxPeriod: Option<string> | null;
  file: File | string | null;
  rates?: AppRateFormItem[];
};

export type TimesheetEmployeeFormRefType = {
  submit: () => void;
};

type TimesheetEmployeeFormProps = {
  formRef?: Ref<TimesheetEmployeeFormRefType>;
  onSubmit?: (values: TimesheetEmployeeFormValues) => void;
  defaultValues?: TimesheetEmployeeFormValues;
  subcontractorId?: string;
};

const initialValues: TimesheetEmployeeFormValues = {
  assignmentRef: null,
  agencyRef: null,
  startDate: null,
  endDate: null,
  siteId: null,
  taxPeriod: null,
  file: null,
};

const TimesheetEmployeeForm = ({
  formRef,
  onSubmit,
  defaultValues = initialValues,
  subcontractorId,
}: TimesheetEmployeeFormProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const paymentModel =
    user?.subcontractorId?.setting?.paymentModel ||
    user?.subcontractorId?.paymentModel;
  const isCIS = isCISPaymentModel(paymentModel);

  const [form] = Form.useForm<TimesheetEmployeeFormValues>();
  const watchValues = Form.useWatch([], form);

  const [taxPeriodOptions, setTaxPeriodOptions] = useState<Option<string>[]>(
    [],
  );

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
  }));

  // Initialize tax period options with WEEKLY frequency as default
  useEffect(() => {
    const taxPeriodOptions = getOptionsAllTaxWeekTaxMonth('WEEKLY')?.map(
      (option) => ({
        label: option.label,
        value: option.value.toString(),
      }),
    );
    setTaxPeriodOptions(taxPeriodOptions);
    // Set default tax period
    const currentTaxPeriod = getCurrentTaxPeriodByFrequency('WEEKLY');
    form.setFieldsValue({
      taxPeriod:
        taxPeriodOptions.find(
          (option) => option.value === currentTaxPeriod?.toString(),
        ) ?? null,
      rates: [{ ...PAYMENT_RATE_DEFAULT, vatRate: '0' }],
    });
  }, [form]);

  const agency = watchValues?.agencyRef?.fullData;

  const handleSelectAssignment = (value: any) => {
    form.setFieldValue('assignmentRef', value);
    const assignmentData = value?.fullData;
    const agencyData = assignmentData?.agencyRef;
    const siteData = assignmentData?.siteRef;
    const rateData = assignmentData?.rates
      ?.map((rate: any) => ({
        ...rate,
        rateId: rate.id || rate.rateId || '',
        rateType: rate.type || rate.rateType || 'RATE',
        rateCode: {
          label: rate.rateCode,
          value: rate.rateCode,
        },
        vatDetails: {
          label: rate.vatDetails,
          value: rate.vatDetails,
        },
        ...(isHolidayPayRate(rate.rateCode) ? { units: 1 } : {}),
      }))
      ?.map((rate: any) => ({
        ...rate,
        net: calculateNetAndTotalFromRate(rate.rate, rate.units, rate.vatRate)
          .net,
        total: calculateNetAndTotalFromRate(rate.rate, rate.units, rate.vatRate)
          .total,
      }));
    if (!agency) {
      if (agencyData)
        form.setFieldValue('agencyRef', {
          label: agencyData?.name,
          value: agencyData.id,
        });
      if (siteData) form.setFieldValue('siteId', siteData?.siteName ?? '');
    }
    if (rateData && rateData.length !== 0) {
      form.setFieldValue('rates', rateData);
    }
  };

  const onFinish = (values: TimesheetEmployeeFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.trace(errorInfo);
  };

  return (
    <div className="TimesheetEmployeeForm">
      <AppCard title={messages['timesheet.addNew']}>
        <AppFormContainer
          form={form}
          initialValues={{
            ...initialValues,
            ...defaultValues,
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="px-4"
          labelCol={{ span: 6 }}
        >
          <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-x-4">
            {!isBureau && (
              <>
                <Form.Item
                  label={messages['common.assignment']}
                  name="assignmentRef"
                  rules={[{ required: true }]}
                >
                  {/* Never fall back to the tenant-wide search: this form is
                      only ever rendered for the signed-in employee, so without
                      their subcontractor id there is nothing safe to list. */}
                  <AppSelectSync
                    placeholder={messages['form.selectAssignment']}
                    apiType="searchEmployeeAssignment"
                    type="employeeAssignment"
                    searchId={subcontractorId}
                    disabled={!subcontractorId}
                    allowClear={false}
                    onChangeValue={handleSelectAssignment}
                  />
                </Form.Item>
                <Form.Item
                  label={messages['common.agencyName']}
                  name="agencyRef"
                >
                  <AppSelect
                    disabled
                    options={[]}
                    placeholder={messages['form.selectAgency']}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item label={messages['common.taxPeriod']} name="taxPeriod">
              <AppSelect
                options={taxPeriodOptions}
                placeholder={messages['form.selectTaxPeriod']}
              />
            </Form.Item>
            {!isIreland && (
              <Form.Item label={messages['common.siteId']} name="siteId">
                <AppInput placeholder="Enter Site ID" />
              </Form.Item>
            )}
            <Form.Item label={isCIS ? messages['common.invoiceDate'] : (messages['common.timesheetDate'] ?? 'Timesheet Date')}>
              <div className="grid grid-cols-2 gap-x-4">
                <Form.Item name="startDate" className="mb-0">
                  <AppDatePicker
                    placeholder={messages['common.startDate']}
                    format={'DD/MM/YYYY'}
                    maxDate={
                      watchValues?.endDate
                        ? moment(watchValues.endDate)
                        : undefined
                    }
                  />
                </Form.Item>
                <Form.Item name="endDate" className="mb-0">
                  <AppDatePicker
                    placeholder={messages['common.endDate']}
                    format={'DD/MM/YYYY'}
                    minDate={
                      watchValues?.startDate
                        ? moment(watchValues.startDate)
                        : undefined
                    }
                  />
                </Form.Item>
              </div>
            </Form.Item>
            <Form.Item label={messages['common.attachment']} name="file">
              <AppUploadFile
                onChange={(file: File) => {
                  form.setFieldsValue({ file });
                }}
              />
            </Form.Item>
          </div>
        </AppFormContainer>
      </AppCard>
      <AppRateForm
        className="mt-4"
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        defaultValues={initialValues}
        listHidden={['total', 'vatDetails', 'vatRate']}
        listDisabled={
          !isBureau
            ? ['net', 'total', 'rateType', 'description', 'rate', 'rateCode']
            : ['net', 'total', 'rateType', 'description']
        }
        hideAdd={!isBureau}
        hideDelete={!isBureau}
      />
    </div>
  );
};

export default TimesheetEmployeeForm;
